import React, { useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { exportAkfaMappingsTemplate, importAkfaMappingsExcel } from "../../../services/akfa/akfaService";
import { downloadBlob, showError } from "../../../layout/akfa/akfaUtils";
import ConflictBanner from "./ConflictBanner";

// Spetsifikatsiya: AKFA_MAPPING_EXCEL_IMPORT_FRONTEND.md — Mapping tabidagi "+ Mapping" yoniga
// "Excel orqali yuklash": shablon yuklab olish + faylni qayta yuklash, bitta modal ichida.

const BTN_PRIMARY =
    "px-4 py-2 text-sm font-semibold text-white bg-slate-800 rounded-md hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed";
const BTN_SECONDARY =
    "px-4 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-md hover:border-indigo-600 hover:text-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed";
const TH_CLASS = "px-2.5 py-2 text-left font-semibold whitespace-nowrap";

function StatCard({ label, value }) {
    return (
        <div className="flex-1 min-w-[90px] rounded-lg border border-gray-200 bg-slate-50 px-3 py-2.5 text-center">
            <div className="text-xl font-bold text-gray-900">{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
        </div>
    );
}

export default function AkfaMappingExcelImportModal({ onClose, onImported }) {
    const { t } = useTranslation();
    const { token } = useSelector((state) => state.user);

    const fileInputRef = useRef(null);
    const importedRef = useRef(false);

    const [file, setFile] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null); // { total, applied, skipped, errors, conflicts }

    const handleClose = () => {
        if (importedRef.current) onImported?.();
        onClose();
    };

    const handleDownloadTemplate = async () => {
        setDownloading(true);
        const response = await exportAkfaMappingsTemplate(token);
        setDownloading(false);
        if (response.success) {
            downloadBlob(response.data, response.fileName);
        } else {
            showError(response, t("akfaIntegrationPage.common.action_error"));
        }
    };

    const handleFileChange = (e) => {
        const selected = e.target.files?.[0] || null;
        if (selected && !selected.name.toLowerCase().endsWith(".xlsx")) {
            showError(null, t("akfaIntegrationPage.excelImport.invalid_extension"));
            e.target.value = "";
            setFile(null);
            return;
        }
        setFile(selected);
        setResult(null);
    };

    const handleUpload = async () => {
        if (!file) {
            showError(null, t("akfaIntegrationPage.excelImport.select_file_first"));
            return;
        }
        setUploading(true);
        const response = await importAkfaMappingsExcel(token, file);
        setUploading(false);
        if (response.success || response.status === 409) {
            const data = response.data || {};
            setResult(data);
            if (response.success && (data.applied || 0) > 0) importedRef.current = true;
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        showError(response, t("akfaIntegrationPage.common.action_error"));
    };

    const conflicts = result?.conflicts || [];
    const errors = result?.errors || [];
    const hasConflicts = conflicts.length > 0;
    const nothingChanged = result && !hasConflicts && errors.length === 0 && (result.applied || 0) === 0;

    return (
        <Dialog open as="div" className="relative z-50" onClose={handleClose}>
            <div className="fixed inset-0 bg-slate-900/50" />
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex items-center justify-center min-h-full p-5">
                    <Dialog.Panel className="w-full max-w-[640px] bg-white rounded-xl shadow-2xl overflow-hidden">
                        <div className="flex items-start justify-between gap-3 px-4 py-4 border-b border-gray-200">
                            <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                                {t("akfaIntegrationPage.excelImport.title")}
                            </Dialog.Title>
                            <button
                                type="button"
                                onClick={handleClose}
                                aria-label={t("akfaIntegrationPage.common.close")}
                                className="flex-none inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-500 hover:bg-slate-50 hover:text-gray-900"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {hasConflicts && (
                            <>
                                <ConflictBanner
                                    title={t("akfaIntegrationPage.mappingModal.conflict_title", { count: conflicts.length })}
                                    text={t("akfaIntegrationPage.excelImport.conflict_text")}
                                    items={conflicts}
                                    renderItem={(item) => (
                                        <>
                                            {item.productName} · {item.textureName} —{" "}
                                            {t("akfaIntegrationPage.mappingModal.conflict_now")}{" "}
                                            {item.currentAkfaProductName ||
                                                t("akfaIntegrationPage.mapping.winpro_unmapped").toLowerCase()}
                                        </>
                                    )}
                                />
                                <p className="px-4 pt-3 text-xs text-gray-500">
                                    {t("akfaIntegrationPage.excelImport.conflict_hint")}
                                </p>
                            </>
                        )}

                        <div className="flex flex-col gap-4 p-4">
                            <div>
                                <div className="text-sm font-semibold text-gray-900 mb-1.5">
                                    {t("akfaIntegrationPage.excelImport.step1_title")}
                                </div>
                                <button type="button" onClick={handleDownloadTemplate} disabled={downloading} className={BTN_SECONDARY}>
                                    {downloading
                                        ? t("akfaIntegrationPage.common.loading")
                                        : t("akfaIntegrationPage.excelImport.download_button")}
                                </button>
                                <p className="mt-1.5 text-xs text-gray-500">{t("akfaIntegrationPage.excelImport.step1_hint")}</p>
                            </div>

                            <div>
                                <div className="text-sm font-semibold text-gray-900 mb-1.5">
                                    {t("akfaIntegrationPage.excelImport.step2_title")}
                                </div>
                                <div className="flex flex-wrap items-center gap-2.5">
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx"
                                        onChange={handleFileChange}
                                        aria-label={t("akfaIntegrationPage.excelImport.step2_title")}
                                        className="text-sm text-gray-700 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border file:border-gray-300 file:bg-white file:text-sm file:font-semibold file:text-gray-900 hover:file:border-indigo-600 hover:file:text-indigo-600"
                                    />
                                    <button type="button" onClick={handleUpload} disabled={!file || uploading} className={BTN_PRIMARY}>
                                        {uploading
                                            ? t("akfaIntegrationPage.excelImport.uploading")
                                            : t("akfaIntegrationPage.excelImport.upload_button")}
                                    </button>
                                </div>
                            </div>

                            {result && (
                                <div className="border-t border-gray-200 pt-4">
                                    <div className="flex gap-2.5">
                                        <StatCard label={t("akfaIntegrationPage.excelImport.stat_total")} value={result.total ?? 0} />
                                        <StatCard label={t("akfaIntegrationPage.excelImport.stat_applied")} value={result.applied ?? 0} />
                                        <StatCard label={t("akfaIntegrationPage.excelImport.stat_skipped")} value={result.skipped ?? 0} />
                                    </div>

                                    {nothingChanged && (
                                        <p className="mt-2.5 text-sm text-gray-500">
                                            {t("akfaIntegrationPage.excelImport.nothing_changed")}
                                        </p>
                                    )}

                                    {errors.length > 0 && (
                                        <div className="mt-3.5">
                                            <div className="text-sm font-semibold text-gray-900 mb-1.5">
                                                {t("akfaIntegrationPage.excelImport.errors_title")} ({errors.length})
                                            </div>
                                            <div className="max-h-52 overflow-auto border border-gray-200 rounded-lg">
                                                <table className="min-w-full text-xs">
                                                    <thead>
                                                        <tr className="bg-slate-800 text-slate-50">
                                                            <th className={TH_CLASS}>{t("akfaIntegrationPage.excelImport.col_row")}</th>
                                                            <th className={TH_CLASS}>
                                                                {t("akfaIntegrationPage.excelImport.col_product_id")}
                                                            </th>
                                                            <th className={TH_CLASS}>
                                                                {t("akfaIntegrationPage.excelImport.col_texture_id")}
                                                            </th>
                                                            <th className={TH_CLASS}>{t("akfaIntegrationPage.excelImport.col_field")}</th>
                                                            <th className={TH_CLASS}>{t("akfaIntegrationPage.excelImport.col_value")}</th>
                                                            <th className="px-2.5 py-2 text-left font-semibold">
                                                                {t("akfaIntegrationPage.excelImport.col_message")}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {errors.map((err, index) => (
                                                            <tr key={index} className="border-b border-gray-200 last:border-0">
                                                                <td className="px-2.5 py-1.5">{err.row}</td>
                                                                <td className="px-2.5 py-1.5">{err.productId ?? "—"}</td>
                                                                <td className="px-2.5 py-1.5">{err.textureId ?? "—"}</td>
                                                                <td className="px-2.5 py-1.5">{err.field ?? "—"}</td>
                                                                <td className="px-2.5 py-1.5">{err.value ?? "—"}</td>
                                                                <td className="px-2.5 py-1.5">{err.message}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <p className="mt-1.5 text-xs text-gray-500">
                                                {t("akfaIntegrationPage.excelImport.errors_hint")}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-1 py-2 text-sm font-semibold text-gray-500 underline"
                            >
                                {t("akfaIntegrationPage.common.close")}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
}
